import { StorageItem } from '../service/constants';
import { login } from './AuthProvider';
import { API_URL } from './Constants';
import { IRefresh } from './model/IRefresh';
import { IWorkingHours } from './model/IWorkingHours';
import { request } from './Provider';
import { parseJwt } from './Utils';

/*
*
* AUTH 
*
**/

const RESOURCE = 'public/refreshMicrosoftToken';

export const requestTokens = async (refreshToken: string): Promise<IRefresh> => (await (await request(
  `${API_URL}/${RESOURCE}`,
  'GET',
  undefined,
  {
    'refresh_token': refreshToken
  }
)).json())

export const setTokens = (value: IRefresh) => localStorage.setItem(StorageItem.Graph, JSON.stringify(value));

const getTokens = (): IRefresh => {
  try {
    return JSON.parse(localStorage.getItem(StorageItem.Graph));
  } catch {
    return undefined;
  }
}

const refreshTokens = async (silent: boolean): Promise<string> => {
  const tokens = getTokens();
  if (!tokens || !tokens.refresh_token) {
    silent || login();
    return undefined;
  }

  try {
    const refreshObj = await requestTokens(tokens.refresh_token);
    setTokens(refreshObj);
    return refreshObj.access_token;
  } catch {
    silent || login();
    return undefined;
  }
}

const getValidToken = (): string => {
  const tokens = getTokens();

  if (!tokens || !tokens.access_token)
    return undefined;

  const jwt = parseJwt(tokens.access_token);
  if (!jwt || !jwt.exp) return undefined;

  const expires = new Date(jwt.exp * 1000);
  if (Date.now() > expires.getTime() - 5 * 60 * 1000)
    return undefined;

  return tokens.access_token;
}

let graphPromise: Promise<string> = null;
const acquireToken = async (silent: boolean = false): Promise<string> => {
  const token = getValidToken();
  if (token) return token;

  if (graphPromise === null) {
    graphPromise = refreshTokens(silent);
    const result = await graphPromise;
    graphPromise = null;
    return result;
  }

  return await graphPromise;
}


/*
*
* GRAPH 
*
**/
const GRAPH_URL = 'https://graph.microsoft.com/v1.0';
const BILLING_INFORMATION = 'String {00062008-0000-0000-c000-000000000046} Id 0x8535';
const extendedProperties = 'singleValueExtendedProperties';

export const graphRequest = async (uri: string, method: 'GET' | 'PATCH' | 'POST', body?: BodyInit) => fetch(uri,
  {
    method,
    body,
    headers: {
      'Authorization': 'Bearer ' + await acquireToken(),
      'Content-Type': 'application/json'
    }
  });


export const getBillingInformation = async (eventId: string): Promise<string | undefined> => {
  const searchParams = new URLSearchParams({
    $expand: `${extendedProperties}($filter=id eq '${BILLING_INFORMATION}')`,
    select: extendedProperties
  })
  const resource = `me/events/${eventId}?${searchParams.toString()}`;

  const event = await (await graphRequest(`${GRAPH_URL}/${resource}`, 'GET')).json();

  if (!event) return undefined;

  const props = event[extendedProperties];

  if (!props || !props[0]) return undefined;

  return props[0].value;
}

export const getEvent =  async (eventId: string) => {
  const resource = `me/events/${eventId}`;
  return (await graphRequest(`${GRAPH_URL}/${resource}`, 'GET')).json();
}


export const setBillingInformation = (eventId: string, billingInformation: string) => {
  const resource = `me/events/${eventId}`;

  return graphRequest(`${GRAPH_URL}/${resource}`, 'PATCH', JSON.stringify({
    [extendedProperties]: [{
      id: BILLING_INFORMATION,
      value: billingInformation
    }]
  }));
}

export const addToMasterCategory = (category: string) => {
  const masterCategoryResource = `me/outlook/masterCategories`;
  try{
    graphRequest(`${GRAPH_URL}/${masterCategoryResource}`, 'POST', JSON.stringify({
      displayName: category,
      color: "preset0"
    }));
  } catch {}
};

export const isAlldayEvent = async (eventId: string): Promise<boolean> => {

  const resource = `me/events/${eventId}?$select=isAllDay`;

  const event = await (await graphRequest(`${GRAPH_URL}/${resource}`, 'GET')).json();

  return !!event.isAllDay;
}

export const getWorkDays = async (): Promise<IWorkingHours> => {

  const resource = `me/mailboxSettings/workinghours`;

  return (await graphRequest(`${GRAPH_URL}/${resource}`, 'GET')).json();
}

export const getPhoto = () => {
  const resource = 'me/photo/$value';

  return graphRequest(`${GRAPH_URL}/${resource}`, 'GET');
}
